import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Star, Check, ExternalLink, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Guest-facing "Rate your driver" page — 1-tap link from WhatsApp/email.
 *
 * UX pattern (review funnel):
 *   • Guest picks 1..5 stars
 *   • 4-5 stars → offer Google / TripAdvisor review CTA
 *   • 1-3 stars → private feedback textarea (protects public reputation)
 *
 * Public — no auth. The booking ref in the URL is the shared secret.
 */
export default function RateDriver() {
  const { ref } = useParams();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stars, setStars] = useState(0);
  const [hoverStars, setHoverStars] = useState(0);
  const [comment, setComment] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/rate/${ref}`);
        setCtx(data);
        if (data.existing_rating?.stars) {
          setStars(data.existing_rating.stars);
          setComment(data.existing_rating.comment || "");
        }
      } catch (e) {
        setError(e?.response?.data?.detail || "This link is no longer valid.");
      } finally {
        setLoading(false);
      }
    })();
  }, [ref]);

  const submit = async () => {
    if (stars < 1) {
      toast.error("Please pick a rating first");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/rate/${ref}`, {
        stars, comment: comment.trim() || null, email: email.trim() || null,
      });
      setResult(data);
      // Auto-redirect to Google review URL for 4-5★ after a short delay.
      if (data.redirect_url && stars >= 4) {
        setTimeout(() => { window.open(data.redirect_url, "_blank"); }, 800);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit rating");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <ScreenShell>
        <Loader2 className="w-6 h-6 animate-spin text-[#C5A059]" />
      </ScreenShell>
    );
  }
  if (error) {
    return (
      <ScreenShell>
        <p className="text-zinc-400 text-sm">{error}</p>
      </ScreenShell>
    );
  }

  // Thank-you screen after submission.
  if (result) {
    const highRating = stars >= 4;
    return (
      <ScreenShell>
        <div className="text-center max-w-md" data-testid="rate-thankyou">
          <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center mb-4">
            <Check className="w-7 h-7 text-emerald-300" />
          </div>
          <h1 className="font-display text-2xl text-white">Thank you!</h1>
          <div className="flex justify-center gap-1 mt-3">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} className={`w-6 h-6 ${n <= stars ? "text-[#C5A059] fill-[#C5A059]" : "text-zinc-700"}`} />
            ))}
          </div>
          {highRating && result.redirect_url && (
            <>
              <p className="text-sm text-zinc-400 mt-6 leading-relaxed">
                Would you also share this on <span className="text-white">Google</span>?
                It takes 30 seconds and helps other travelers find us.
              </p>
              <a
                href={result.redirect_url}
                target="_blank" rel="noopener noreferrer"
                data-testid="google-review-cta"
                className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black font-medium text-sm transition-colors"
              >
                Post on Google <ExternalLink className="w-4 h-4" />
              </a>
            </>
          )}
          {!highRating && (
            <p className="text-sm text-zinc-400 mt-6 leading-relaxed">
              Thanks for the honest feedback. The Juke Transfers team will personally look at your comment
              and get back to you if you left an email.
            </p>
          )}
        </div>
      </ScreenShell>
    );
  }

  // Rating form
  const showComment = stars > 0 && stars <= 3;
  const showBoost = stars >= 4;
  return (
    <ScreenShell>
      <div className="max-w-md w-full" data-testid="rate-form">
        <p className="text-[11px] uppercase tracking-[0.35em] text-[#C5A059] text-center">
          Juke Transfers
        </p>
        <h1 className="font-display text-3xl text-white text-center mt-3">
          How was your ride?
        </h1>
        {ctx?.pickup && ctx?.dropoff && (
          <p className="text-xs uppercase tracking-widest text-zinc-500 text-center mt-3">
            {ctx.pickup} → {ctx.dropoff}
          </p>
        )}

        {/* 5-star selector */}
        <div className="flex justify-center gap-2 mt-10" onMouseLeave={() => setHoverStars(0)}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              onClick={() => setStars(n)}
              onMouseEnter={() => setHoverStars(n)}
              data-testid={`rate-star-${n}`}
              className="p-2 transition-transform hover:scale-110"
              aria-label={`${n} star${n > 1 ? "s" : ""}`}
            >
              <Star
                className={`w-12 h-12 transition-colors ${
                  n <= (hoverStars || stars)
                    ? "text-[#C5A059] fill-[#C5A059]"
                    : "text-zinc-700"
                }`}
              />
            </button>
          ))}
        </div>

        {showBoost && (
          <p className="text-center text-sm text-emerald-300 mt-2 animate-in fade-in">
            Awesome — we&rsquo;ll ask if you can share this on Google.
          </p>
        )}

        {showComment && (
          <div className="mt-8 space-y-3">
            <label className="block text-xs uppercase tracking-[0.2em] text-zinc-500">
              What could we improve? <span className="text-zinc-600">(kept private)</span>
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              data-testid="rate-comment"
              placeholder="Anything that would have made your ride better…"
              className="w-full bg-zinc-900 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-[#C5A059]/40 outline-none"
            />
            <label className="block text-xs uppercase tracking-[0.2em] text-zinc-500 mt-3">
              Email (optional — for us to follow up)
            </label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              data-testid="rate-email"
              placeholder="you@example.com"
              className="w-full bg-zinc-900 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-[#C5A059]/40 outline-none"
            />
          </div>
        )}

        <button
          onClick={submit}
          disabled={submitting || stars < 1}
          data-testid="rate-submit"
          className="mt-8 w-full h-14 rounded-xl bg-[#C5A059] hover:bg-[#D4AF37] disabled:opacity-40 disabled:cursor-not-allowed text-black font-semibold text-base transition-colors"
        >
          {submitting ? "Submitting…" : stars < 1 ? "Pick a rating" : "Submit"}
        </button>
        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-600 text-center mt-6">
          {ctx?.invoice_ref ? `Receipt ${ctx.invoice_ref}` : ctx?.ref}
        </p>
      </div>
    </ScreenShell>
  );
}

function ScreenShell({ children }) {
  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6">
      {children}
    </div>
  );
}
